/**
 * useThemeObserver — watches which [data-section-theme] section currently
 * dominates the top ~40% of the viewport, then reads that section's
 * computed CSS custom properties (--nav-bg, --nav-text, --nav-accent, etc.)
 * so the navbar and cursor can adapt to any colour scheme, not just
 * binary light/dark.
 *
 * Each section should set:
 *   data-section-theme="anything"  (just needs to exist)
 *   --nav-bg: rgba(...)            background the navbar picks up
 *   --nav-text: #fff               text colour the navbar uses
 *   --nav-accent: #3d5af1          accent colour (active link, underline)
 *   --nav-border: rgba(...)        subtle border colour
 *   --section-bg: #0a0a0a          the section's own background (for wipe fill)
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const DEFAULTS = {
  bg: 'rgba(10,10,10,0.6)',
  text: '#f5f2ec',
  accent: '#3d5af1',
  border: 'rgba(245,242,236,0.08)',
  sectionBg: '#0a0a0a',
};

function readProp(el, prop, fallback) {
  const val = getComputedStyle(el).getPropertyValue(prop).trim();
  return val || fallback;
}

export function useThemeObserver() {
  const [theme, setTheme] = useState(DEFAULTS);
  const rafId = useRef(null);

  const calculate = useCallback(() => {
    const sections = document.querySelectorAll('[data-section-theme]');
    if (!sections.length) return;

    const vh = window.innerHeight;
    const zone = vh * 0.4; // top 40% of viewport — where the navbar lives
    let bestEl = null;
    let bestScore = -1;

    sections.forEach((section) => {
      const r = section.getBoundingClientRect();
      // How much of the top-zone does this section cover?
      const overlap = Math.max(0, Math.min(zone, r.bottom) - Math.max(0, r.top));
      if (overlap > bestScore) {
        bestScore = overlap;
        bestEl = section;
      }
    });

    if (bestEl) {
      const next = {
        bg: readProp(bestEl, '--nav-bg', DEFAULTS.bg),
        text: readProp(bestEl, '--nav-text', DEFAULTS.text),
        accent: readProp(bestEl, '--nav-accent', DEFAULTS.accent),
        border: readProp(bestEl, '--nav-border', DEFAULTS.border),
        sectionBg: readProp(bestEl, '--section-bg', DEFAULTS.sectionBg),
      };
      setTheme((prev) => {
        // Only update if something actually changed (avoids needless re-renders)
        if (
          prev.bg === next.bg &&
          prev.text === next.text &&
          prev.accent === next.accent &&
          prev.border === next.border &&
          prev.sectionBg === next.sectionBg
        )
          return prev;
        return next;
      });
    }
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(calculate);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', calculate, { passive: true });
    // Initial read (after paint so CSS vars are available)
    requestAnimationFrame(() => requestAnimationFrame(calculate));

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', calculate);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [calculate]);

  return theme;
}

export default useThemeObserver;
